import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/* Access tokens live 15 minutes. Without a silent refresh the user gets
   thrown to the login screen mid-session even though their refresh cookie
   is still valid for days. One shared in-flight promise so a burst of
   parallel 401s triggers exactly one refresh call. */
let refreshing = null;
function silentRefresh() {
  if (!refreshing) {
    refreshing = axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  useEffect(() => {
    if (interceptorRef.current !== null) return;
    interceptorRef.current = axios.interceptors.response.use(
      r => r,
      async error => {
        const cfg = error.config || {};
        const is401 = error.response?.status === 401;
        const isAuthCall = typeof cfg.url === 'string' &&
          (cfg.url.includes('/api/auth/refresh') || cfg.url.includes('/api/auth/login') || cfg.url.includes('/api/auth/register'));
        if (is401 && !isAuthCall && !cfg._retried) {
          cfg._retried = true;
          try {
            await silentRefresh();
            return axios({ ...cfg, withCredentials: true });
          } catch {
            setUser(false);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) axios.interceptors.response.eject(interceptorRef.current);
      interceptorRef.current = null;
    };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // The access cookie may simply have aged out — try the refresh cookie
      // before deciding the session is gone.
      try {
        const { data } = await silentRefresh();
        setUser(data?.email ? data : false);
        if (!data?.email) {
          const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
          setUser(me.data);
        }
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  /* Keep the access token warm while the tab is open, and re-verify when the
     user comes back to a tab that has been idle. */
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => { silentRefresh().catch(() => {}); }, 12 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') silentRefresh().catch(() => {}); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [user]);

  const login = useCallback(async (email, password, remember = false) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password, remember }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch (e) { console.error('Logout error:', e); }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) { console.error('Refresh error:', e); }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
